// RenderForm.tsx
/*eslint-disable*/
import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from 'react';

import { Frame, Element, SerializedNodes, Editor } from '@craftjs/core';
import SnapCanvas from '../SnapCanvas/SnapCanvas';
import InputField from '../BuilderInputField/InputField';
import CheckboxField from '../Checkbox/CheckboxField';
import MultilineTextbox from '../MultilineTextbox/MultilineTextbox';
import UploadArea from '../UploadArea/UploadArea';
import DropdownField from '../DropdownField/DropdownField';
import DatePickerField from '../DatePicker/DatePicker';
import TimePickerField from '../TimePickerField/TimePickerField';
import { InteractionModeProvider } from '../contexts/InteractionMode';
import FormValueCollector, {
  FormValueCollectorRef,
} from '../FormValueCollector/FormValueCollector';
import { submitReport } from '../../api/report/post';
import { useDispatch, useSelector } from 'react-redux';
import { setReportDataFromBins } from '../../Redux/slices/reportDataSlice';
import {
  StagedUploadsContext,
  StagedUploadsProvider,
} from '../../contexts/StagedUploadsContext';

export type RenderFormHandle = {
  submit: () => Promise<void>;
};

interface RenderFormInterface {
  serializedData: SerializedNodes;
  type: 'REPORT' | 'INVENTORIES' | 'CHECKLISTS' | 'TRACKER' | 'SUMMARY';
  resetView?: () => void;
}

//for re-rendering the editor when the serialized data changes
function hashString(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return String(h);
}

const RenderForm = forwardRef<RenderFormHandle, RenderFormInterface>(
  ({ serializedData, type, resetView }, ref) => {
    const dispatch = useDispatch();
    const tz = useSelector((state: any) => state.currentSite.timezone);
    const formRef = useRef<FormValueCollectorRef>(null);

    // ✅ use staged (not files)
    const { staged, clearAll } = React.useContext(StagedUploadsContext);

    // We'll use this to query the live DOM for required fields
    const editorRootRef = useRef<HTMLDivElement | null>(null);

    const {
      inventoryTabItems,
      inventoryTab,
      reportsTabItems,
      reportsTab,
      checklistTab,
      checklistTabItems,
      trackerTabItems,
      trackerTab,
    } = useSelector((state: any) => state.tab);

    const {
      reportLayouts,
      inventoryLayouts,
      checklistLayouts,
      trackerLayouts,
    } = useSelector((state: any) => state.reportLayouts);

    const editorKey = useMemo(() => {
      try {
        return hashString(JSON.stringify(serializedData ?? {}));
      } catch {
        return String(Date.now()); // fallback
      }
    }, [serializedData]);

    /** DOM-based validation: find any wrappers marked data-required="true" that are empty */
    const validateRequiredFields = (): { ok: boolean; missing: string[] } => {
      const root = editorRootRef.current;
      if (!root) return { ok: true, missing: [] };

      const wrappers = Array.from(
        root.querySelectorAll<HTMLElement>('[data-required="true"]')
      );

      const missing: string[] = [];
      let firstInvalid: HTMLElement | null = null;

      for (const wrap of wrappers) {
        // Prefer real controls first
        const control =
          (wrap.querySelector(
            'input:not([type="hidden"]), textarea, select'
          ) as
            | HTMLInputElement
            | HTMLTextAreaElement
            | HTMLSelectElement
            | null) || null;

        let empty = false;

        if (control) {
          if (control.tagName.toLowerCase() === 'input') {
            const inp = control as HTMLInputElement;
            if (inp.type === 'checkbox') {
              empty = !inp.checked;
            } else {
              empty = !String(inp.value ?? '').trim();
            }
          } else if (control.tagName.toLowerCase() === 'textarea') {
            empty = !String(
              (control as HTMLTextAreaElement).value ?? ''
            ).trim();
          } else if (control.tagName.toLowerCase() === 'select') {
            empty = !String((control as HTMLSelectElement).value ?? '').trim();
          }
        } else {
          // Last-ditch: any input we can find
          const anyInput = wrap.querySelector(
            'input'
          ) as HTMLInputElement | null;
          if (anyInput) {
            empty =
              anyInput.type === 'checkbox'
                ? !anyInput.checked
                : !String(anyInput.value ?? '').trim();
          }
        }

        if (empty) {
          const labelEl =
            (wrap.querySelector('label') as HTMLElement | null) ||
            (wrap.querySelector('[class*="label"]') as HTMLElement | null);
          const labelText =
            labelEl?.textContent?.replace('*', '').trim() || 'Required field';

          missing.push(labelText);
          if (!firstInvalid) firstInvalid = control as HTMLElement | null;
        }
      }

      firstInvalid?.focus?.();
      return { ok: missing.length === 0, missing };
    };

    const handleSubmit = useCallback(async () => {
      // 1) Validate required fields BEFORE building payload
      const { ok, missing } = validateRequiredFields();
      if (!ok) {
        alert(`Please fill all required fields:\n\n- ${missing.join('\n- ')}`);
        return;
      }

      // 2) Collect values (preserve your payload shape)
      const values = formRef.current?.getValues();
      if (!values) {
        console.error('No form data collected');
        return;
      }

      // 3) Pull (optional) staged file from context Map (first one wins)
      //    staged is a Map<string, File>, so we can read its iterator safely
      const iter = staged.entries().next();
      const firstEntry: [string, File] | undefined = iter.done
        ? undefined
        : iter.value;
      const uploadFieldKey = firstEntry?.[0];
      const stagedFile = firstEntry?.[1];

      const formData = new FormData();
      let prevLabel: unknown;

      // Keep your triple-append behavior; skip the UploadArea key if we’re sending a file
      for (const [
        key,
        { fieldValue, labelValue, previewLabel },
      ] of Object.entries(values)) {
        if (uploadFieldKey && key === uploadFieldKey) continue;

        formData.append(`${key}`, String(fieldValue));
        formData.append(`${key}`, String(labelValue));
        formData.append(`${key}`, String(previewLabel));

        if (previewLabel === true) {
          prevLabel = fieldValue;
        }
      }

      formData.append('DocPreviewLabel', String(prevLabel));

      // 4) Doc metadata (unchanged)
      if (type === 'REPORT') {
        formData.append('DocName', reportsTabItems[reportsTab]);
        formData.append('DocType', 'REPORT');
        formData.append(
          'LayoutId',
          (Array.from(Object.values(reportLayouts))[reportsTab] as any)
            ?.layoutId
        );
        formData.append(
          'creationDateId',
          (Array.from(Object.values(reportLayouts))[reportsTab] as any)
            ?.creationDateId
        );
      }

      if (type === 'INVENTORIES') {
        formData.append('DocName', inventoryTabItems[inventoryTab]);
        formData.append('DocType', 'INVENTORY');
        formData.append(
          'LayoutId',
          (Array.from(Object.values(inventoryLayouts))[inventoryTab] as any)
            ?.layoutId
        );
        formData.append(
          'creationDateId',
          (Array.from(Object.values(inventoryLayouts))[inventoryTab] as any)
            ?.creationDateId
        );
      }

      if (type === 'CHECKLISTS') {
        formData.append('DocName', checklistTabItems[checklistTab]);
        formData.append('DocType', 'CHECKLIST');
        formData.append(
          'LayoutId',
          (Array.from(Object.values(checklistLayouts))[checklistTab] as any)
            ?.layoutId
        );
        formData.append(
          'creationDateId',
          (Array.from(Object.values(checklistLayouts))[checklistTab] as any)
            ?.creationDateId
        );
      }

      if (type === 'TRACKER') {
        formData.append('DocName', trackerTabItems[trackerTab]);
        formData.append('DocType', 'TRACKER');
        formData.append(
          'LayoutId',
          (Array.from(Object.values(trackerLayouts))[trackerTab] as any)
            ?.layoutId
        );
        formData.append(
          'creationDateId',
          (Array.from(Object.values(trackerLayouts))[trackerTab] as any)
            ?.creationDateId
        );
      }

      // 5) If a file is staged, attach it + the node id so the backend can map it
      if (stagedFile && uploadFieldKey) {
        formData.append('file', stagedFile);
        formData.append('uploadFieldKey', uploadFieldKey);
        formData.append('uploadOriginalName', stagedFile.name);
      }

      try {
        const response = await submitReport(formData);
        dispatch(
          setReportDataFromBins({
            bins: (response as any).reports,
            timezone: tz,
          })
        );

        // clear staged files after a successful submit
        clearAll();

        alert('Report submitted!');
        resetView && resetView();
      } catch (error) {
        console.error('Error submitting report:', error);
        alert('Submission failed');
      }
    }, [
      // context
      staged,
      clearAll,
      // validation + collector
      validateRequiredFields,
      formRef,
      // type + tabs/layouts
      type,
      reportsTabItems,
      reportsTab,
      reportLayouts,
      inventoryTabItems,
      inventoryTab,
      inventoryLayouts,
      checklistTabItems,
      checklistTab,
      checklistLayouts,
      trackerTabItems,
      trackerTab,
      trackerLayouts,
      // redux/env
      dispatch,
      tz,
      resetView,
    ]);

    // Expose imperative API to parent
    useImperativeHandle(ref, () => ({ submit: handleSubmit }), [handleSubmit]);

    return (
      <>
        <StagedUploadsProvider>
          <InteractionModeProvider value="FILL">
            {/* Root wrapper for DOM validation */}
            <div ref={editorRootRef}>
              <Editor
                key={editorKey}
                enabled={false}
                resolver={{
                  SnapCanvas,
                  InputField,
                  CheckboxField,
                  MultilineTextbox,
                  UploadArea,
                  DropdownField,
                  DatePickerField,
                  TimePickerField,
                }}
              >
                <Frame data={serializedData}>
                  <Element is={SnapCanvas} canvas id="snap_canvas" />
                </Frame>
                <FormValueCollector ref={formRef} />
              </Editor>
            </div>
          </InteractionModeProvider>
        </StagedUploadsProvider>
      </>
    );
  }
);

RenderForm.displayName = 'RenderForm';
export default RenderForm;
